import { useState } from 'react';
import { LandingPageThemeId } from '@client/new/common/landingPageThemes';
import { resolvedVisualVersion } from '@client/new/common/landingPageVisualVersion';
import { makeAsync } from '@client/new/helpers/async';
import { Async } from '@client/new/helpers/types';
import { useSetMyLandingPageLookMutation } from '../../../../../generated/graphql';
import { PageLook, themeOrDefault, themeToSave } from './pageLook';

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const usePageLook = (initialLook: PageLook) => {
  const [look, setLook] = useState<PageLook>(() => ({
    ...initialLook,
    visualVersion: resolvedVisualVersion(initialLook.visualVersion),
  }));
  const [selectedTheme, setSelectedTheme] = useState<LandingPageThemeId>(
    themeOrDefault(initialLook.savedTheme),
  );
  const [isSwitchOpen, setIsSwitchOpen] = useState(false);
  const [switchStatus, setSwitchStatus] = useState<Async<null>>(
    makeAsync.notFetched(),
  );
  const [setMyLandingPageLook] = useSetMyLandingPageLookMutation();

  const pendingTheme = themeToSave(look, selectedTheme);

  const confirmSwitch = async () => {
    setSwitchStatus(makeAsync.fetching());
    try {
      await setMyLandingPageLook({ variables: { theme: selectedTheme } });
      setLook({ visualVersion: 2, savedTheme: selectedTheme });
      setSwitchStatus(makeAsync.fetched(null));
      setIsSwitchOpen(false);
    } catch (error) {
      setSwitchStatus(makeAsync.error(errorMessage(error)));
    }
  };

  const savePendingTheme = async () => {
    if (!pendingTheme) return;
    await setMyLandingPageLook({ variables: { theme: pendingTheme } });
    setLook((current) => ({ ...current, savedTheme: pendingTheme }));
  };

  return {
    visualVersion: look.visualVersion,
    selectedTheme,
    selectTheme: setSelectedTheme,
    isThemeDirty: pendingTheme !== null,
    savePendingTheme,
    isSwitchOpen,
    openSwitch: () => {
      setSwitchStatus(makeAsync.notFetched());
      setIsSwitchOpen(true);
    },
    closeSwitch: () => setIsSwitchOpen(false),
    confirmSwitch,
    switchStatus,
    justSwitched: switchStatus.type === 'fetched',
  };
};
